/* istanbul ignore file */
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import { HeroModule } from '@pcln/marketing'
import pclnExperimentation from '@pcln/experimentation'
import { Box } from 'pcln-design-system'
import { type IImageType } from '@pcln/contentful-components'
import SignedInStatusProvider from '@/context/SignedInStatusContext'
import useContentfulVariation from '@/hooks/useContentfulVariation'
import { SearchFormDesktop } from '@/components/SearchForm'
import TravelChatBot from '@/components/TravelChatBot'
import analytics from '@/shared-utils/analytics'
import { useFeature } from '@pcln/feature-components'
import getCurrentTab from '@/shared-utils/getCurrentTab'
import type { ProductType } from '@/types'
import { hasContentTypeId } from '@/shared-utils/content-model-type-guards'
import HomeContent from './HomeContent'
import SearchFormExperimentWapper from '../SearchFormExperiment/SearchFormExperimentWrapper'
import ConditionalRender, { Original, Experiment } from '../ConditionalRender'

const kaleyImage =
  'https://s1.pclncdn.com/design-assets/next-landing/assets/images/hero/kaley-image-default.jpg'

const defaultHeroProps = {
  backgroundImage: {
    url: `${kaleyImage}?opto&auto=avif,webp`,
    altText: 'Go to your happy price'
  },
  imageClassName: 'hero-image',
  shouldDisplaySilhouette: true
}

const protocolRegex = /(^\w+:|^)/g
const queryRegex = /\?(.*)/g

function trimContentfulAssetUrl(url: string) {
  return url.replace(protocolRegex, '').replace(queryRegex, '')
}

const handleHeroImageClick = (url: string) => {
  window.open(url)
}

function isIImageType(content: unknown): content is IImageType {
  return hasContentTypeId(content) && content.contentTypeId === 'image'
}

export default function HomeDesktop() {
  const { push, query, pathname } = useRouter()
  const currentTab = getCurrentTab(query)
  const darkFill = useFeature('siteExperience.darkFill')
  const heroImage = useContentfulVariation('heroImage', isIImageType)
  const { openPenny, askPrompt } = query

  useEffect(() => {
    pclnExperimentation.fireImpression('MKTG_HP_DIGIOH')
  }, [])

  const openPennyProps = {
    openPenny: typeof openPenny === 'string' ? openPenny : '',
    askPrompt: typeof askPrompt === 'string' ? askPrompt : ''
  }
  const onFormChange = (productType: ProductType) => {
    void push(
      {
        pathname,
        query: {
          ...query,
          tab: productType
        }
      },
      undefined,
      { shallow: true }
    )
    analytics.fireGA4Event({
      event: 'internal_element',
      attributes: {
        type: 'search_form',
        page_name: 'homepage',
        link_name: productType === 'vacations' ? 'bundle_and_save' : productType
      }
    })
  }
  const heroImageContentfulAsset = heroImage?.asset
  const heroImageContentfulCTA = heroImage?.callToAction
  const heroProps = heroImageContentfulAsset?.file?.url
    ? {
        ...defaultHeroProps,
        searchForms: (
          <SignedInStatusProvider>
            <SearchFormDesktop
              currentTab={currentTab}
              onFormChange={onFormChange}
            />
          </SignedInStatusProvider>
        ),
        backgroundImage: {
          url: `https:${trimContentfulAssetUrl(
            heroImageContentfulAsset.file.url
          )}?fm=webp&w=1180&h=840&q=80`,
          altText: heroImageContentfulAsset.title ?? ''
        },
        handleImageClick: heroImageContentfulCTA?.url
          ? (event: KeyboardEvent) => {
              const { url } = heroImageContentfulCTA
              if (url) {
                event.preventDefault()
                handleHeroImageClick(url)
              }
            }
          : null,
        ariaLabel: heroImageContentfulCTA
          ? heroImageContentfulCTA.ariaLabel
          : null,
        isDarkerFill: darkFill
      }
    : {
        ...defaultHeroProps,
        searchForms: (
          <SignedInStatusProvider>
            <SearchFormDesktop
              currentTab={currentTab}
              onFormChange={onFormChange}
            />
          </SignedInStatusProvider>
        ),
        handleImageClick: (event: KeyboardEvent) => {
          event.preventDefault()
          handleHeroImageClick('https://www.priceline.com/vacationpackages')
        },
        ariaLabel: 'vacation packages',
        isDarkerFill: darkFill
      }
  return (
    <>
      <ConditionalRender experimentCode="HP_SEARCH_FORM_HORIZON_COMPONENTS_DESKTOP">
        <Original>
          <Head>
            <link
              rel="preload"
              as="image"
              fetchPriority="high"
              href={heroProps.backgroundImage.url}
            />
          </Head>
          <Box style={{ overflowX: 'hidden' }}>
            <Box height="100%" data-testid="hero-outer-box">
              <HeroModule {...heroProps} />
            </Box>
            <SignedInStatusProvider>
              <HomeContent />
            </SignedInStatusProvider>
          </Box>
        </Original>
        <Experiment>
          <Head>
            <link
              rel="preload"
              as="image"
              fetchPriority="high"
              href={heroProps.backgroundImage.url}
            />
          </Head>
          <Box style={{ overflowX: 'hidden' }}>
            <Box height="100%" data-testid="hero-outer-box-experiment">
              <HeroModule
                {...heroProps}
                searchForms={
                  <SignedInStatusProvider>
                    <SearchFormExperimentWapper
                      currentTab={currentTab}
                      onFormChange={onFormChange}
                    />
                  </SignedInStatusProvider>
                }
              />
            </Box>
            <SignedInStatusProvider>
              <HomeContent />
            </SignedInStatusProvider>
          </Box>
        </Experiment>
      </ConditionalRender>
      <TravelChatBot
        pageName="homepage"
        enableChatBot={currentTab === 'hotels'}
        {...openPennyProps}
      />
    </>
  )
}
